import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons'
import { Box, Text, Flex, TextInput, Button, Select } from '@mantine/core'
import { useRef, useEffect, useCallback, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useQueryGetUserAddresses,
  useQueryGetUserTaxProfile,
} from '@flecto/core/shared'
import { Trans, useTranslation } from '@flecto/translations'
import { type CoreItem } from '@flecto/types'
import { Icon, Badge, Pulse, RichTextEditor } from '@flecto/ui'
import { AlertActionable } from '@flecto/ui/@shared/AlertActionable'
import { SelectCustomCategories } from '@flecto/ui/@shared/SelectCustomCategories/SelectCustomCategories'
import { useAddressInline, useIntercom } from '@flecto/utils'

import { useFlattenItemCategories } from '../../../categories/hooks/useFlattenItemCategories'
import { useItemGeneratorAI } from '../../hooks/useItemGeneratorAI'
import { GridItemPhotosManager } from '../GridItemPhotosManager/GridItemPhotosManager'
import { useModalAddress } from '../ModalAddress/useModalAddress'
import { type InsurableStatus, useFormContext } from './FormItem'
import { InsuranceBadge } from './InsuranceBadge'
import classes from './styles.module.css'

type TabPropertiesProps = {
  isInsurable: InsurableStatus
  changeInsurability: (value: InsurableStatus) => void
  fetchInsurability: () => void
}

export function TabProperties({
  isInsurable,
  fetchInsurability,
  changeInsurability,
}: TabPropertiesProps) {
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const { data: addresses } = useQueryGetUserAddresses()
  const { refetch } = useQueryGetUserTaxProfile()
  const [startTime, setStartTime] = useState<number>()
  const [oldDescription, setOldDescription] = useState<string | undefined>(
    form.values.description || ''
  )
  const { showIntercomWithMessage } = useIntercom()

  const {
    getDescriptionWithAI,
    generateListPhotosWithAI,
    cancelGenerateAI,
    isLoading: isLoadingAI,
  } = useItemGeneratorAI()

  const { openModal } = useModalAddress({
    addressId: form.values.owner_address_id,
    onSuccess: (address) => {
      form.setFieldValue('owner_address_id', address.id)
      refetch()
    },
  })

  const isGeneratingRef = useRef('')
  const previousValueRef = useRef<string | undefined>(form.values.description)
  const id =
    isGeneratingRef?.current === 'true' ? 'rich-text-editor-description' : ''

  const track = useAnalytics('segment')
  const category = form.getInputProps('category_id')
  const getAddressLabel = useAddressInline()

  const { categories } = useFlattenItemCategories() || []

  const handleCategoryChange = useCallback(
    (dataP: string | number | null) => {
      changeInsurability?.('loading')

      if (categories && dataP && Array.isArray(categories)) {
        const from =
          categories.find((cat) => cat.id === category.value)?.name || ''
        const to = categories.find((cat) => cat.id === dataP)?.name || ''
        track('ITEM__CATEGORY_EDITED', { from, to })
      }

      category.onChange(dataP)
      fetchInsurability?.()
    },
    [categories, category, changeInsurability, track]
  )

  const handleChangeDescription = useCallback(
    (text?: string) => {
      if (isLoadingAI.all) return
      form.setFieldValue('description', text)
    },
    [isLoadingAI, form]
  )

  const handleBlurDescription = () => {
    const currentValue = form.values.description
    if (currentValue !== previousValueRef.current) {
      track('ITEM__DESCRIPTION_EDITED', {
        from: previousValueRef.current ?? '',
        to: currentValue as string,
      })
    }
    previousValueRef.current = currentValue
  }

  const handleGenerateContentWithAI = async () => {
    const title = form.values.title ?? ''

    if (!title) {
      return
    }

    if (form.values.description && form.values.description.length > 0) {
      setOldDescription(form.values.description)
    } else {
      setOldDescription('')
    }

    form.setFieldValue('description', '')
    form.values.description = ''

    setStartTime(performance.now())

    getDescriptionWithAI(title, (message) => {
      const content = `${form.values.description}${message}`
      form.setFieldValue('description', content)
      form.values.description = content
    })

    generateListPhotosWithAI(title)
  }

  const handleCancelGenerateContentWithAI = () => {
    cancelGenerateAI()

    form.values.description = ' '
    form.values.category_id = ''
    form.values.photos_attributes = []
  }

  useEffect(() => {
    if ((addresses ?? []).length > 0 && !form.values.owner_address_id) {
      const mainAddress = addresses?.find((address) => address.main)
      if (mainAddress) {
        form.setFieldValue('owner_address_id', mainAddress.id)
      }
    }
  }, [addresses])

  useEffect(() => {
    if (form.values.owner_address_id && form.values.tax_rate === '0') {
      form.setFieldValue('tax_rate', 'standard')
    }
  }, [form.values.owner_address_id])

  useEffect(() => {
    if (startTime && !isLoadingAI.all) {
      const endTime = performance.now()

      track('ITEM__AIGEN_CLICK', {
        responseTime: endTime - startTime,
      })
    }
    if (!isLoadingAI.description) {
      if (!form.values.description || form.values.description?.length === 0) {
        form.values.description = oldDescription
      }
    }
  }, [isLoadingAI.all, startTime, oldDescription])

  return (
    <Box>
      <Text fw={500}>{t('general.item_information')}</Text>
      <Text c='dimmed'>{t('general.item_information_desc')}</Text>
      <Flex mt='md' direction='column' gap='md'>
        <Flex direction='column'>
          <Box style={{ position: 'relative' }}>
            <TextInput
              size='md'
              label={
                <Flex
                  w='100%'
                  direction='row'
                  justify='space-between'
                  align='center'
                >
                  <Text component='span'>
                    {t('general.field_title.title')}
                    <Text component='span' c='red' fz='md'>
                      {' '}
                      *
                    </Text>
                  </Text>
                  <InsuranceBadge
                    isInsurable={isInsurable}
                    hasRequiredFields={
                      !!form.values.title && !!form.values.category_id
                    }
                  />
                </Flex>
              }
              placeholder={t('general.field_title.placeholder')}
              readOnly={isLoadingAI.all}
              disabled={isLoadingAI.all}
              className={classes.titleInput}
              {...form.getInputProps('title')}
              onChange={(event) => {
                form.setFieldValue('title', event.target.value)
                changeInsurability?.('pending')
              }}
              onBlur={() => fetchInsurability?.()}
            />
            {isInsurable === 'not_eligible' &&
              form.values.title &&
              form.values.category_id && (
                <Text c='gray.7' fz='sm' mt='sm'>
                  <Trans
                    components={{
                      ContactLink: (
                        <Text
                          component='span'
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          c='gray.7'
                          fz='sm'
                          fw={500}
                          onClick={() =>
                            showIntercomWithMessage(
                              t('insurance_intercom', {
                                replace: {
                                  itemName: form.values.title,
                                },
                              })
                            )
                          }
                        />
                      ),
                    }}
                    i18nKey='items.item_not_eligible_contact'
                    ns='saas'
                  />
                </Text>
              )}
          </Box>
          <AlertActionable>
            <AlertActionable.Title>
              {t('generate_item_description.title')}
              <Badge ml='sm' color='green'>
                {t('generate_item_description.new_badge')}
              </Badge>
            </AlertActionable.Title>
            <AlertActionable.Description mr='xs'>
              {t('generate_item_description.description')}
            </AlertActionable.Description>
            <AlertActionable.Actions>
              <Flex gap='sm'>
                {isLoadingAI.all && (
                  <Button
                    variant='outline'
                    onClick={handleCancelGenerateContentWithAI}
                    data-testid='cancel-ai'
                  >
                    {t('generate_item_description.cancel')}
                  </Button>
                )}
                <Button
                  disabled={
                    String(form.values.title).length < 3 || !form.values.title
                  }
                  loading={isLoadingAI.all}
                  onClick={handleGenerateContentWithAI}
                  leftSection={<Icon icon={faWandMagicSparkles} />}
                  data-testid='generate-ai'
                >
                  {t('generate_item_description.action')}
                </Button>
              </Flex>
            </AlertActionable.Actions>
          </AlertActionable>
        </Flex>
        <Flex direction='column' gap='sm'>
          <Flex gap='xs' align='center'>
            <Text fw='500'>{t('general.field_description.title')} </Text>
            <Text c='red.6'>*</Text>
            {isLoadingAI.description && (
              <Pulse color='blue'>
                <Badge
                  color='blue'
                  leftSection={<Icon icon={faWandMagicSparkles} size='xs' />}
                >
                  {t('generate_item_description.loading')}
                </Badge>
              </Pulse>
            )}
          </Flex>
          <RichTextEditor
            wrapperContainerId={id}
            placeholder={t('general.field_description.placeholder')}
            updateIfChanged
            value={form.values.description || oldDescription}
            error={form.getInputProps('description').error}
            onBlur={handleBlurDescription}
            onChange={handleChangeDescription}
          />
        </Flex>
        <Flex>
          <SelectCustomCategories
            {...category}
            w='100%'
            label={t('general.field_category.title')}
            placeholder={t('general.field_category.placeholder')}
            withAsterisk
            onChange={handleCategoryChange}
            data={categories}
            onCategoryCreated={(newCategory) => {
              form.setFieldValue('category_id', newCategory.id)
              form.values.category_id = newCategory.id
              handleCategoryChange(`${newCategory.id}`)
            }}
          />
        </Flex>
        <Flex direction='column'>
          <Select
            label={t('general.address.title')}
            placeholder={t('general.address.placeholder')}
            withAsterisk
            data={(addresses ?? []).map((item) => ({
              value: item.id,
              label: getAddressLabel(item, true),
            }))}
            allowDeselect={false}
            {...form.getInputProps('owner_address_id')}
          />
          <Box mt='md'>
            <Button variant='outline' size='sm' onClick={openModal}>
              {t('general.add_new_address')}
            </Button>
          </Box>
        </Flex>
        <Flex direction='column' w='100%'>
          <Flex gap='xs' align='center' mb='sm'>
            <Text fw='500'>{t('general.images')}</Text>
            <Text c='red.6'>*</Text>
            {isLoadingAI.images && (
              <Pulse color='blue'>
                <Badge
                  color='blue'
                  leftSection={<Icon icon={faWandMagicSparkles} size='xs' />}
                >
                  {t('generate_item_description.loading')}
                </Badge>
              </Pulse>
            )}
            <Text size='sm' c='red.6'>
              {form.getInputProps('photos_attributes').error}
            </Text>
          </Flex>
          <GridItemPhotosManager
            hasError={Boolean(form.getInputProps('photos_attributes').error)}
            product={form.values as unknown as CoreItem}
            onChange={(photosAttrs) => {
              form.setFieldValue('photos_attributes', photosAttrs)
            }}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
